import React from 'react';
import ReactDOM from 'react-dom';
import { Button } from '@components/atoms/Button';
import { formatForDisplay } from '@utils/formatDate';
import type { PrecedentConflict } from '@utils/precedentConflicts';

// Detection lives in @utils/precedentConflicts so the form, the Dependencies
// tab and the schedule views all judge a conflict the same way. Re-exported
// here because existing imports point at this file.
export type { PrecedentConflict };

interface PrecedentConflictModalProps {
  isOpen: boolean;
  taskName: string;
  proposedStartDate: string;
  proposedEndDate: string;
  conflicts: PrecedentConflict[];
  onCancel: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  /**
   * Label for the proceed button. Defaults to saving, since the form is the
   * common caller; dragging a bar on the timeline passes "Move anyway".
   */
  confirmLabel?: string;
}

export const PrecedentConflictModal: React.FC<PrecedentConflictModalProps> = ({
  isOpen,
  taskName,
  proposedStartDate,
  proposedEndDate,
  conflicts,
  onCancel,
  onConfirm,
  isLoading = false,
  confirmLabel = 'SAVE ANYWAY',
}) => {
  if (!isOpen) return null;

  const taskConflicts = conflicts.filter((c) => c.type === 'task');
  const eventConflicts = conflicts.filter((c) => c.type === 'event');

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex: 99999 }}>
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center mb-4">
          <div className="flex-shrink-0">
            <svg
              className="h-6 w-6 text-amber-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Precedent Conflict Warning
            </h3>
          </div>
        </div>

        <div className="mb-6">
          {/* Neutral about *which* side moved: the same clash appears whether
              the user shifted this task's dates or picked a precedent that
              finishes later. */}
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            <strong>"{taskName}"</strong> is scheduled to start before its
            precedents finish. These dependencies are meant to complete before
            this task can start.
          </p>

          {/* Proposed Dates */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
              This task's dates:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-700 dark:text-blue-300 font-medium">
                  Start Date:
                </span>
                <br />
                <span className="text-gray-600 dark:text-gray-300">
                  {formatForDisplay(proposedStartDate)}
                </span>
              </div>
              <div>
                <span className="text-blue-700 dark:text-blue-300 font-medium">
                  End Date:
                </span>
                <br />
                <span className="text-gray-600 dark:text-gray-300">
                  {formatForDisplay(proposedEndDate)}
                </span>
              </div>
            </div>
          </div>

          {/* Conflicting Tasks */}
          {taskConflicts.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Conflicting Precedent Tasks:
              </h4>
              {taskConflicts.map((conflict, index) => (
                <div
                  key={`task-${conflict.id}-${index}`}
                  className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-4 w-4 text-red-400 mt-0.5"
                        fill="currentColor"
                        viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-2 flex-1">
                      <p className="text-sm font-medium text-red-800 dark:text-red-200">
                        {conflict.name}
                      </p>
                      <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                        Ends on {formatForDisplay(conflict.endDate)} -{' '}
                        {conflict.conflictReason}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Conflicting Events */}
          {eventConflicts.length > 0 && (
            <div className="space-y-3 mb-4">
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Conflicting Precedent Events:
              </h4>
              {eventConflicts.map((conflict, index) => (
                <div
                  key={`event-${conflict.id}-${index}`}
                  className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-4 w-4 text-orange-400 mt-0.5"
                        fill="currentColor"
                        viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-2 flex-1">
                      <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                        {conflict.name}
                      </p>
                      <p className="text-xs text-orange-600 dark:text-orange-300 mt-1">
                        Occurs on {formatForDisplay(conflict.endDate)} -{' '}
                        {conflict.conflictReason}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-4 w-4 text-amber-400 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-2">
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  <strong>Note:</strong> You can proceed. The precedent
                  relationships are kept as they are, so the schedule will stay
                  out of order until one of the two dates moves.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3">
          {/* This is the cancel path — it was labelled "OK", which reads as
              confirmation and sent people out of the flow by accident. */}
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            Go Back
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent inline-block mr-2"></div>
                Processing...
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
